import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
  apiFetch,
  clearSessionToken,
  getSessionToken,
  setSessionToken,
} from '../lib/apiClient';
import { isSupabaseConfigured, supabase } from '../lib/supabaseClient';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import {
  canUseNativeGoogleSignIn,
  signInWithNativeGoogleIdToken,
  signOutNativeGoogle,
} from '../lib/nativeGoogleAuth';
import {
  canUseAppleSignIn,
  isAppleSignInCancelled,
  signInWithApple,
  signOutNativeApple,
} from '../lib/appleAuth';
import PhoneVerificationModal from '../components/PhoneVerificationModal';
import SignInOptions from '../components/SignInOptions';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { initRemotePush, unregisterRemotePush } from '../lib/pushNotifications';

/** Users who have not completed phone OTP (verify once at signup). */
function computeIsNewUnverifiedUser(userData) {
  if (!userData) return false;
  return userData.phone_verified !== true;
}

const AuthContext = createContext(null);
const AUTH_PROVIDER = (process.env.REACT_APP_AUTH_PROVIDER || 'supabase').toLowerCase();

function isOAuthCallbackInProgress(pathname, search, hash) {
  if (pathname === '/auth/callback') return true;
  if (search?.includes('code=') || search?.includes('cap_app=')) return true;
  if (hash?.includes('access_token=') || hash?.includes('session_id=')) return true;
  return false;
}
const LEGACY_AUTH_URL = process.env.REACT_APP_AUTH_URL || '';

/** Absolute OAuth redirect URL (must match Supabase allow list). Env required on native; web falls back to current origin. */
function resolveWebAuthRedirect() {
  const fromEnv =
    process.env.REACT_APP_WEB_AUTH_REDIRECT ||
    process.env.REACT_APP_NATIVE_AUTH_REDIRECT ||
    '';
  if (fromEnv) return fromEnv;
  if (typeof window !== 'undefined' && !Capacitor.isNativePlatform()) {
    return `${window.location.origin}/auth/callback`;
  }
  return '';
}

/** Native browser OAuth: hosted callback forwards ?code= via skipdq:// (PKCE verifier only in main WebView). */
function getNativeSupabaseOAuthRedirectTo() {
  const base = resolveWebAuthRedirect();
  if (!base) return '';
  try {
    const u = new URL(base);
    u.searchParams.set('cap_app', '1');
    return u.toString();
  } catch {
    const join = base.includes('?') ? '&' : '?';
    return `${base}${join}cap_app=1`;
  }
}

function openSupabaseGoogleInBrowser(redirectUrl) {
  return supabase.auth
    .signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl,
        queryParams: { prompt: 'select_account' },
        skipBrowserRedirect: true,
      },
    })
    .then(async ({ data, error }) => {
      if (error || !data?.url) {
        console.error('Native OAuth URL generation failed', error);
        toast.error(error?.message || 'Could not start Google browser sign-in');
        return;
      }
      await Browser.open({
        url: data.url,
        presentationStyle: 'fullscreen',
      });
    });
}

export function AuthProvider({ children }) {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isNewUnverifiedUser, setIsNewUnverifiedUser] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const [appleSigningIn, setAppleSigningIn] = useState(false);
  const [demoLoginEnabled, setDemoLoginEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiFetch('/api/auth/demo-enabled')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled) setDemoLoginEnabled(Boolean(data?.enabled));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const applyUserState = useCallback((userData) => {
    setUser(userData);
    setIsNewUnverifiedUser(computeIsNewUnverifiedUser(userData));
  }, []);

  /**
   * Load the current user from the API.
   * @returns {Promise<{ ok: boolean, status?: number, detail?: string, user?: object|null }>}
   */
  const checkAuth = useCallback(async () => {
    try {
      const response = await apiFetch('/api/auth/me');
      if (response.ok) {
        const userData = await response.json();
        applyUserState(userData);
        return { ok: true, status: response.status, user: userData };
      }
      let detail = '';
      try {
        const body = await response.json();
        detail = typeof body?.detail === 'string' ? body.detail : JSON.stringify(body);
      } catch {
        try {
          detail = await response.text();
        } catch {
          detail = '';
        }
      }
      console.error('Auth check failed:', response.status, detail || response.statusText);
      applyUserState(null);
      return { ok: false, status: response.status, detail: detail || response.statusText, user: null };
    } catch (error) {
      const message =
        (typeof error?.message === 'string' && error.message) ||
        String(error || 'Auth check failed');
      console.error('Auth check failed:', message, error);
      applyUserState(null);
      return { ok: false, detail: message, user: null };
    } finally {
      setLoading(false);
    }
  }, [applyUserState]);

  const handlePhoneVerified = useCallback((updatedUser) => {
    setUser(updatedUser);
    setIsNewUnverifiedUser(false);
  }, []);

  // Do NOT request notification permission here — iOS often swallows the system
  // dialog when this runs in the same tick as Google/Apple OAuth return.
  // VendorDashboard (and student flows that need it) call initRemotePush after
  // the UI is stable / on an explicit tap. We only warm local permission flag lightly.
  useEffect(() => {
    if (!user) return;
    const wantsPush =
      user.phone_verified === true ||
      user.role === 'vendor' ||
      user.role === 'admin';
    if (!wantsPush) return;
    // Non-interactive: register only if already granted (no prompt during login).
    initRemotePush({ interactive: false }).catch(() => {});
  }, [user?.user_id, user?.phone_verified, user?.role]);

  const bootstrapSupabaseSession = useCallback(async () => {
    if (AUTH_PROVIDER === 'legacy' || !isSupabaseConfigured || !supabase) {
      return;
    }
    try {
      const { data } = await supabase.auth.getSession();
      const accessToken = data?.session?.access_token;
      if (accessToken) {
        setSessionToken(accessToken);
      }
    } catch (error) {
      console.error('Supabase session bootstrap failed:', error);
    }
  }, []);

  useEffect(() => {
    if (isOAuthCallbackInProgress(location.pathname, location.search, location.hash)) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const init = async () => {
      setLoading(true);
      await bootstrapSupabaseSession();
      if (!cancelled) {
        await checkAuth();
      }
    };
    init();
    return () => {
      cancelled = true;
    };
  }, [
    location.pathname,
    location.search,
    location.hash,
    bootstrapSupabaseSession,
    checkAuth,
  ]);

  useEffect(() => {
    if (AUTH_PROVIDER === 'legacy' || !isSupabaseConfigured || !supabase) {
      return undefined;
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN') {
        const accessToken = session?.access_token;
        if (accessToken) {
          setSessionToken(accessToken);
        }
        // Always refresh profile from API so UI never shows a stale user after OAuth.
        await checkAuth();
      } else if (event === 'INITIAL_SESSION') {
        const accessToken = session?.access_token;
        if (accessToken) {
          setSessionToken(accessToken);
        }
        if (
          accessToken
          && !isOAuthCallbackInProgress(
            window.location.pathname,
            window.location.search,
            window.location.hash,
          )
        ) {
          await checkAuth();
        }
      } else if (event === 'SIGNED_OUT') {
        clearSessionToken();
        applyUserState(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [checkAuth, applyUserState]);

  const login = () => {
    clearSessionToken();
    applyUserState(null);

    const redirectUrl = Capacitor.isNativePlatform()
      ? getNativeSupabaseOAuthRedirectTo()
      : resolveWebAuthRedirect();
    if (AUTH_PROVIDER !== 'legacy' && isSupabaseConfigured && supabase) {
      if (!redirectUrl || !redirectUrl.startsWith('http')) {
        console.error(
          'Set REACT_APP_WEB_AUTH_REDIRECT (or REACT_APP_NATIVE_AUTH_REDIRECT) for OAuth on native.',
        );
        return;
      }
    }
    if (AUTH_PROVIDER === 'legacy' || !isSupabaseConfigured || !supabase) {
      if (!LEGACY_AUTH_URL) {
        console.error('Legacy auth selected but REACT_APP_AUTH_URL is missing');
        return;
      }
      window.location.href = `${LEGACY_AUTH_URL}/?redirect=${encodeURIComponent(redirectUrl)}`;
      return;
    }
    if (Capacitor.isNativePlatform()) {
      if (canUseNativeGoogleSignIn()) {
        (async () => {
          try {
            await signOutNativeGoogle();
            const { idToken, accessToken, nonce } = await signInWithNativeGoogleIdToken();
            console.log(
              '[Auth] token being passed to signInWithIdToken:',
              idToken?.substring(0, 50),
              { hasAccessToken: Boolean(accessToken), hasNonce: Boolean(nonce) },
            );
            const { data, error } = await supabase.auth.signInWithIdToken({
              provider: 'google',
              token: idToken,
              ...(accessToken ? { access_token: accessToken } : {}),
              ...(nonce ? { nonce } : {}),
            });
            if (error || !data?.session?.access_token) {
              console.error('Supabase Google id-token sign-in failed', error);
              toast.error(
                error?.message ||
                  'Google sign-in could not create a session. Trying browser login…',
              );
              await openSupabaseGoogleInBrowser(redirectUrl);
              return;
            }
            setSessionToken(data.session.access_token);
            const authResult = await checkAuth();
            if (!authResult?.ok) {
              const statusPart = authResult?.status ? ` (${authResult.status})` : '';
              toast.error(
                authResult?.detail
                  ? `Signed in with Google but server rejected the session${statusPart}: ${authResult.detail}`
                  : `Signed in with Google but server rejected the session${statusPart}`,
              );
              return;
            }
            setSignInOpen(false);
          } catch (e) {
            console.error('Native Google sign-in failed; using in-app browser OAuth', e);
            toast.error(
              typeof e?.message === 'string' && e.message
                ? e.message
                : 'Google sign-in failed. Trying browser login…',
            );
            try {
              await signOutNativeGoogle();
            } catch {
              /* ignore */
            }
            await openSupabaseGoogleInBrowser(redirectUrl);
          }
        })();
        return;
      }
      openSupabaseGoogleInBrowser(redirectUrl).catch((e) => {
        console.error(e);
        toast.error('Google sign-in is not available right now');
      });
      return;
    }

    supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl,
        queryParams: { prompt: 'select_account' },
      },
    });
  };

  const loginWithApple = async () => {
    if (appleSigningIn) return false;
    setAppleSigningIn(true);
    try {
      await signOutNativeApple();
      const apple = await signInWithApple();
      // An Apple session is a backend token, so drop any Supabase session that
      // would otherwise overwrite it on the next session bootstrap.
      if (AUTH_PROVIDER !== 'legacy' && supabase) {
        const { data } = await supabase.auth.getSession();
        if (data?.session) {
          await supabase.auth.signOut({ scope: 'local' });
        }
      }
      const response = await apiFetch('/api/auth/apple', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_token: apple.idToken,
          nonce: apple.nonce,
          user: apple.user,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.session_token) {
        throw new Error(data?.detail || 'Sign in with Apple failed');
      }
      setSessionToken(data.session_token);
      await checkAuth();
      setSignInOpen(false);
      return true;
    } catch (error) {
      if (!isAppleSignInCancelled(error)) {
        console.error('Sign in with Apple failed', error);
        toast.error(
          typeof error?.message === 'string' && error.message
            ? error.message
            : 'Sign in with Apple failed',
        );
      }
      return false;
    } finally {
      setAppleSigningIn(false);
    }
  };

  const loginWithEmail = async (email, password) => {
    try {
      if (AUTH_PROVIDER !== 'legacy' && supabase) {
        const { data } = await supabase.auth.getSession();
        if (data?.session) {
          await supabase.auth.signOut({ scope: 'local' });
        }
      }
      const response = await apiFetch('/api/auth/demo-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.session_token) {
        throw new Error(
          response.status === 429
            ? 'Too many attempts. Please wait a minute and try again.'
            : data?.detail || 'Sign in failed',
        );
      }
      setSessionToken(data.session_token);
      await checkAuth();
      setSignInOpen(false);
      return true;
    } catch (error) {
      toast.error(error?.message || 'Sign in failed');
      return false;
    }
  };

  const signInWithGoogleFromChooser = () => {
    setSignInOpen(false);
    login();
  };

  /** Entry point for sign-in buttons: offers Apple / email alongside Google where available. */
  const openSignIn = () => {
    if (!canUseAppleSignIn() && !demoLoginEnabled) {
      login();
      return;
    }
    setSignInOpen(true);
  };

  const logout = async () => {
    const sessionToken = getSessionToken();
    try {
      await unregisterRemotePush();
    } catch (e) {
      console.warn('Push unregister on logout failed:', e);
    }
    clearSessionToken();
    applyUserState(null);

    try {
      if (Capacitor.isNativePlatform() && canUseNativeGoogleSignIn()) {
        await signOutNativeGoogle();
      }
      await signOutNativeApple();
      if (AUTH_PROVIDER !== 'legacy' && supabase) {
        await supabase.auth.signOut();
      }
      await apiFetch('/api/auth/logout', {
        method: 'POST',
        headers: sessionToken ? { Authorization: `Bearer ${sessionToken}` } : undefined,
      });
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const updateUserRole = async (role) => {
    try {
      const response = await apiFetch('/api/auth/role', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role })
      });
      if (response.ok) {
        const updatedUser = await response.json();
        if (updatedUser?.session_token) {
          setSessionToken(updatedUser.session_token);
        }
        applyUserState(updatedUser);
        return true;
      }
    } catch (error) {
      console.error('Role update failed:', error);
    }
    return false;
  };

  const showPhoneModal = Boolean(user && isNewUnverifiedUser && !loading);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser: applyUserState,
        loading,
        isNewUnverifiedUser,
        login,
        loginWithApple,
        openSignIn,
        canUseAppleSignIn: canUseAppleSignIn(),
        appleSigningIn,
        demoLoginEnabled,
        loginWithEmail,
        logout,
        checkAuth,
        updateUserRole,
      }}
    >
      <div className={showPhoneModal ? 'pointer-events-none select-none' : undefined}>
        {children}
      </div>
      <Dialog open={signInOpen} onOpenChange={setSignInOpen}>
        <DialogContent className="max-w-sm rounded-2xl" data-testid="sign-in-dialog">
          <DialogHeader>
            <DialogTitle>Sign in to SkipdQ</DialogTitle>
            <DialogDescription>Order ahead and skip the line.</DialogDescription>
          </DialogHeader>
          <SignInOptions
            onGoogle={signInWithGoogleFromChooser}
            onApple={loginWithApple}
            showApple={canUseAppleSignIn()}
            appleLoading={appleSigningIn}
            showEmail={demoLoginEnabled}
            onEmail={loginWithEmail}
            onLegalNavigate={() => setSignInOpen(false)}
          />
        </DialogContent>
      </Dialog>
      {showPhoneModal && (
        <PhoneVerificationModal open onVerified={handlePhoneVerified} />
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
